import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { WorkspaceProvider } from './hooks/useWorkspace';
import ScrollToTop from './components/ScrollToTop';
import CookieConsentBanner from './components/CookieConsentBanner';
import { usePageViewTracking } from './hooks/usePageViewTracking';
import { useAnalyticsIdentify, useAnalyticsWorkspaceProps } from './hooks/useAnalyticsIdentify';

// Landing page loaded eagerly (most common entry point, critical for LCP)
import LandingPage from './pages/Landing';

// Lazy-loaded pages
const LoginPage = React.lazy(() => import('./pages/auth/Login'));
const SignupPage = React.lazy(() => import('./pages/auth/Signup'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPassword'));
const DashboardPage = React.lazy(() => import('./pages/Dashboard'));
const DirectoryPage = React.lazy(() => import('./pages/Directory'));
const ProfilePage = React.lazy(() => import('./pages/Profile'));
const PricingPage = React.lazy(() => import('./pages/Pricing'));
const BillingSuccess = React.lazy(() => import('./pages/billing/Success'));
const BillingCancel = React.lazy(() => import('./pages/billing/Cancel'));
const TermsPage = React.lazy(() => import('./pages/legal/Terms'));
const PrivacyPage = React.lazy(() => import('./pages/legal/Privacy'));
const CookiesPage = React.lazy(() => import('./pages/legal/Cookies'));
const DisclaimerPage = React.lazy(() => import('./pages/legal/Disclaimer'));
const SecurityPage = React.lazy(() => import('./pages/legal/Security'));

// Content hub pages
const SaaSMetricsPage = React.lazy(() => import('./pages/content/SaaSMetrics'));
const MrrGuidePage = React.lazy(() => import('./pages/content/MrrGuide'));
const ChurnRateGuidePage = React.lazy(() => import('./pages/content/ChurnRateGuide'));
const NrrGuidePage = React.lazy(() => import('./pages/content/NrrGuide'));
const ArrGuidePage = React.lazy(() => import('./pages/content/ArrGuide'));
const ValuationGuidePage = React.lazy(() => import('./pages/content/ValuationGuide'));
const HealthScoreGuidePage = React.lazy(() => import('./pages/content/HealthScoreGuide'));

// Settings pages (named exports need wrapper)
const AccountSettingsPage = React.lazy(() =>
  import('./pages/settings').then((m) => ({ default: m.AccountSettingsPage }))
);
const WorkspaceSettingsPage = React.lazy(() =>
  import('./pages/settings').then((m) => ({ default: m.WorkspaceSettingsPage }))
);

// Layout
import Layout from './components/Layout';


function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <WorkspaceProvider>
      <AnalyticsWorkspaceIdentify />
      {children}
    </WorkspaceProvider>
  );
}

function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

function AnalyticsIdentify() {
  useAnalyticsIdentify();
  return null;
}

function AnalyticsWorkspaceIdentify() {
  useAnalyticsWorkspaceProps();
  return null;
}

function App() {
  usePageViewTracking();

  useEffect(() => {
    // Clear stale dev state from localStorage (panel removed)
    localStorage.removeItem('plevits_dev_state');
    // Signal to prerenderer that the page content is ready
    document.dispatchEvent(new Event('prerender-ready'));
  }, []);

  return (
    <AuthProvider>
      <AnalyticsIdentify />
      <ScrollToTop />
      <CookieConsentBanner />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Root - shows landing for guests, redirects to dashboard for auth users */}
          <Route path="/" element={<RootRedirect />} />

          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/directory/:slug" element={<ProfilePage />} />
          <Route path="/example-profile" element={<ProfilePage />} />

          {/* Billing pages (protected) */}
          <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
          <Route path="/billing/cancel" element={<BillingCancel />} />

          {/* Content hub */}
          <Route path="/saas-metrics" element={<SaaSMetricsPage />} />
          <Route path="/saas-metrics/mrr" element={<MrrGuidePage />} />
          <Route path="/saas-metrics/churn-rate" element={<ChurnRateGuidePage />} />
          <Route path="/saas-metrics/net-revenue-retention" element={<NrrGuidePage />} />
          <Route path="/saas-metrics/arr" element={<ArrGuidePage />} />
          <Route path="/saas-metrics/saas-valuation" element={<ValuationGuidePage />} />
          <Route path="/saas-metrics/health-score" element={<HealthScoreGuidePage />} />

          {/* Legal pages */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/security" element={<SecurityPage />} />

          {/* Protected routes */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<DashboardPage />} />
            {/* Settings routes */}
            <Route path="settings" element={<Navigate to="/settings/account" replace />} />
            <Route path="settings/account" element={<AccountSettingsPage />} />
            <Route path="settings/workspace" element={<WorkspaceSettingsPage />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}

export default App;
